import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../slices/store';
import api from './api';
import type { ChatSession, ChatMessage } from './types';
import ChatMessageComponent from './ChatMessage';
import ChatInput from './ChatInput';
import SessionList from './SessionList';
import RotatingLoadingMessage from './RotatingLoadingMessage';
import './CortexChat.css';

/**
 * CortexChat - Main Chat Component
 * Manages chat state, sessions, and integrates all child components
 *
 * Optimizations:
 * - useCallback for event handlers to prevent unnecessary re-renders
 * - useMemo for expensive computations
 * - Debounced resize handler
 * - Optimized session loading with AbortController
 */

// Category-specific suggested questions (moved outside component to prevent recreation)
const categoryQuestions: Record<string, string[]> = {
  opportunities: [
    'Show me all available opportunities.',
    'What opportunities are currently open?',
    'Which opportunities are closing this month?',
  ],
  lineItems: [
    'Show me my line items.',
    'List all active line items.',
    'Which line items have the highest spend?',
  ],
  accounts: [
    'Show me account details or account information.',
    'Show all orders linked to my account.',
    'What is the name of my account?',
  ],
  orders: [
    'Show me all my orders.',
    'List all active orders.',
    'Which orders are currently running or ongoing or active?',
  ],
};


const CortexChat: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get user info from Redux state
  const user = useSelector((state: RootState) => state.login.user);
  const userId: number | null = user?.user?.users_id || null;
  const userType: string = user?.user?.user_type || 'guest';

  // Scroll to bottom of messages - memoized
  const scrollToBottom = useCallback((): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);


  // Load all sessions - memoized with useCallback
  const loadSessions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.getSessions(userId, userType);
      if (response.success) {
        setSessions(response.sessions);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load chat sessions');
    }
  }, [userId, userType]);

  // Load sessions on mount and when user changes
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Cleanup on unmount - cancel pending requests
  useEffect(() => {
    return () => {
      api.cancelAllRequests();
    };
  }, []);

  // Clear cache when user changes (logout/login)
  useEffect(() => {
    api.clearCache();
  }, [userId]);

  // Load conversation - memoized with useCallback
  const loadConversation = useCallback(async (sessionId: string): Promise<void> => {
    try {
      const response = await api.loadConversation(sessionId, userId, userType);
      if (response.success) {
        // Convert history to messages format
        const conversationMessages: ChatMessage[] = [];
        response.history.forEach((exchange) => {
          conversationMessages.push({
            message_text: exchange.question,
            message_type: 'question',
            created_at: exchange.timestamp,
          });
          conversationMessages.push({
            message_text: exchange.answer,
            message_type: 'answer',
            key_insights: exchange.summary,
            data_summary: exchange.data_summary,
            visualization_data: exchange.visualization,
            created_at: exchange.timestamp,
          });
        });
        setMessages(conversationMessages);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError('Failed to load conversation');
    }
  }, [userId, userType]);

  // Load conversation when session changes
  useEffect(() => {
    if (currentSessionId) {
      loadConversation(currentSessionId);
    }
  }, [currentSessionId, loadConversation]);

  // Send message - memoized with useCallback
  const handleSendMessage = useCallback(async (question: string): Promise<void> => {
    setError(null);
    setIsLoading(true);

    // Clear suggested questions immediately when a new question is asked
    setSuggestedQuestions([]);

    // Add question to messages immediately for better UX
    const questionMessage: ChatMessage = {
      message_text: question,
      message_type: 'question',
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, questionMessage]);

    try {
      const response = await api.askQuestion(question, currentSessionId, userId, userType);

      if (response.success && response.answer) {
        // Log Cortex query processing info to browser console (dev only)
        // console.group('%c🔍 Cortex Query Processing', 'color: #667eea; font-weight: bold; font-size: 14px;');
        // console.log('%c📝 Question Asked:', 'color: #10b981; font-weight: bold;', question);
        // if (response.cortex_query) {
        //   console.log('%c🧠 Cortex Query (Natural Language):', 'color: #0ea5e9; font-weight: bold;');
        //   console.log(response.cortex_query);
        // }
        // if (response.sql_query) {
        //   console.log('%c💾 Generated SQL Query:', 'color: #d946ef; font-weight: bold;');
        //   console.log(response.sql_query);
        // }
        // if (response.processing_time_ms) {
        //   console.log('%c⏱️ Processing Time:', 'color: #f59e0b; font-weight: bold;', `${(response.processing_time_ms / 1000).toFixed(2)}s (${response.processing_time_ms.toFixed(2)}ms)`);
        // }
        // console.groupEnd();

        // Add answer to messages
        const answerMessage: ChatMessage = {
          message_text: response.answer,
          message_type: 'answer',
          key_insights: response.summary,
          data_summary: response.data_summary,
          visualization_data: response.visualization,
          created_at: new Date().toISOString(),
          cortex_query: response.cortex_query,
          sql_query: response.sql_query,
          processing_time_ms: response.processing_time_ms,
        };
        setMessages((prev) => [...prev, answerMessage]);

        // Update current session ID if it was a new conversation
        if (!currentSessionId && response.session_id) {
          setCurrentSessionId(response.session_id);
        }

        // Update suggested questions
        setSuggestedQuestions(response.suggested_questions || []);

        // Reload sessions to update sidebar
        loadSessions();
      } else {
        throw new Error(response.error || 'Failed to get answer');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      console.error('Failed to send message:', err);
      setError(errorMessage);

      // Add error message
      const errorMsg: ChatMessage = {
        message_text: `Error: ${errorMessage}`,
        message_type: 'answer',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [currentSessionId, userId, userType, loadSessions]);

  // New chat - memoized
  const handleNewChat = useCallback((): void => {
    setCurrentSessionId(null);
    setMessages([]);
    setSuggestedQuestions([]);
    setError(null);

    // Close sidebar on mobile after creating new chat
    if (window.innerWidth <= 1024) {
      setIsSidebarOpen(false);
    }
  }, []);

  // Select session - memoized
  const handleSelectSession = useCallback((sessionId: string): void => {
    setCurrentSessionId(sessionId);
    setMessages([]);
    setSuggestedQuestions([]);
    setError(null);

    // Close sidebar on mobile after selecting a session
    if (window.innerWidth <= 1024) {
      setIsSidebarOpen(false);
    }
  }, []);

  // Delete session - memoized
  const handleDeleteSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      const response = await api.clearConversation(sessionId, userId, userType);
      if (response.success) {
        // If deleted current session, clear it
        if (sessionId === currentSessionId) {
          handleNewChat();
        }
        // Reload sessions
        loadSessions();
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete conversation');
    }
  }, [userId, userType, currentSessionId, handleNewChat, loadSessions]);

  // Rename session - memoized
  const handleRenameSession = useCallback(async (sessionId: string, newTitle: string): Promise<void> => {
    try {
      const response = await api.updateSessionTitle(sessionId, newTitle, userId, userType);
      if (response.success) {
        loadSessions();
      }
    } catch (err) {
      console.error('Failed to rename session:', err);
      setError('Failed to rename conversation');
    }
  }, [userId, userType, loadSessions]);

  // Toggle sidebar - memoized
  const toggleSidebar = useCallback((): void => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  // Toggle category - memoized
  const handleCategoryClick = useCallback((category: string): void => {
    setActiveCategory((prev) => prev === category ? null : category);
  }, []);

  // Clear active category - memoized
  const handleClearCategory = useCallback((): void => {
    setActiveCategory(null);
  }, []);

  return (
    <div className="cortex-chat-app">
      {/* Backdrop overlay for mobile */}
      {isSidebarOpen && (
        <div className="sidebar-backdrop" onClick={toggleSidebar}></div>
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <SessionList
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onNewChat={handleNewChat}
          onRenameSession={handleRenameSession}
        />
      </div>

      {/* Semicircular toggle button - outside sidebar for constant visibility */}
      <button
        onClick={toggleSidebar}
        className={`sidebar-toggle-btn ${isSidebarOpen ? 'open' : 'closed'}`}
        title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
        type="button"
      >
        {isSidebarOpen ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Main Chat Area */}
      <div className="main-chat-area">
        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)} className="close-error" type="button">✕</button>
          </div>
        )}

        {/* Top Bar with Smart Questions */}
        {/* <div className="top-bar">
          <div className="top-bar-content">
            <div className="smart-questions-section">
              <div className="lightbulb">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="lightbulb-icon">
                  <path d="M9 18h6M10 22h4M15 8a5 5 0 1 0-6 0c0 2 1 3 1 5h4c0-2 1-3 1-5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span>Get Smart Questions About:</span>
            </div>
            <div className="category-tabs">
              <button
                className={`category-tab ${activeCategory === 'opportunities' ? 'active' : ''}`}
                type="button"
                onClick={() => handleCategoryClick('opportunities')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Opportunities
              </button>
              <button
                className={`category-tab ${activeCategory === 'lineItems' ? 'active' : ''}`}
                type="button"
                onClick={() => handleCategoryClick('lineItems')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Line Items
              </button>
              <button
                className={`category-tab ${activeCategory === 'accounts' ? 'active' : ''}`}
                type="button"
                onClick={() => handleCategoryClick('accounts')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Accounts
              </button>
              <button
                className={`category-tab ${activeCategory === 'orders' ? 'active' : ''}`}
                type="button"
                onClick={() => handleCategoryClick('orders')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M9 2v4m6-4v4M4 9h16M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Orders
              </button>
            </div>
          </div>
        </div> */}

        {/* Messages */}
        <div className="messages-container">
          {messages.length === 0 && !isLoading && (
            <div className="welcome-message">
              <div className="welcome-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1 className="welcome-title">Hello There!</h1>
              <p className="welcome-text">
                  Your AI partner for data analysis and insights. Ask me anything!
              </p>
            </div>
          )}

          {messages.map((message, idx) => (
            <ChatMessageComponent
              key={idx}
              message={message}
              type={message.message_type}
            />
          ))}

          {isLoading && (
            <div className="cortex-loading-indicator">
              <div className="loading-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
              <RotatingLoadingMessage />
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <ChatInput
          onSendMessage={handleSendMessage}
          suggestedQuestions={suggestedQuestions}
          isLoading={isLoading}
          placeholder="Ask a question about your data..."
          activeCategory={activeCategory}
          categoryQuestions={categoryQuestions}
          onClearCategory={handleClearCategory}
        />
      </div>
    </div>
  );
};

export default CortexChat;
